import React, { useMemo } from "react";
import styled from "styled-components";
import { FaCalendarAlt, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { toLocalYmd } from "../utils/localDate";

const startOfWeek = (date) => {
  const next = new Date(date);
  const day = next.getDay() || 7;
  next.setDate(next.getDate() - day + 1);
  return next;
};

const formatMonthKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

/**
 * Universal Period & Date Picker for all Bill Sarthi reports.
 * Supports date range (fromDate & toDate), single date (date & onDateChange), or month.
 */
export default function ReportPeriodPicker({
  fromDate,
  toDate,
  onChange,
  date,
  onDateChange,
  month,
  onMonthChange,
  mode, // 'day' | 'month' toggle if applicable
  onModeChange,
}) {
  const presets = useMemo(() => {
    const now = new Date();
    const todayYmd = toLocalYmd(now);
    const weekStartYmd = toLocalYmd(startOfWeek(now));
    const monthStartYmd = toLocalYmd(new Date(now.getFullYear(), now.getMonth(), 1));
    const monthKey = formatMonthKey(now);

    return [
      { id: "today", label: "Today", from: todayYmd, to: todayYmd, date: todayYmd, month: monthKey },
      { id: "week", label: "Weekly", from: weekStartYmd, to: todayYmd, date: weekStartYmd, month: monthKey },
      { id: "month", label: "Monthly", from: monthStartYmd, to: todayYmd, date: monthStartYmd, month: monthKey },
    ];
  }, []);

  // Determine active preset
  const activePreset = useMemo(() => {
    if (fromDate && toDate) {
      return presets.find((p) => p.from === fromDate && p.to === toDate)?.id || "custom";
    }
    if (date) {
      return presets.find((p) => p.date === date)?.id || "custom";
    }
    if (month) {
      return presets.find((p) => p.month === month)?.id || "custom";
    }
    return "custom";
  }, [fromDate, toDate, date, month, presets]);

  const handlePresetSelect = (preset) => {
    if (onChange) {
      onChange({ from: preset.from, to: preset.to, date: preset.date, month: preset.month });
    }
    if (onDateChange) {
      onDateChange(preset.date);
    }
    if (onMonthChange) {
      onMonthChange(preset.month);
    }
  };

  const handlePrevDay = () => {
    if (!date) return;
    const d = new Date(date);
    d.setDate(d.getDate() - 1);
    const newDate = toLocalYmd(d);
    if (onDateChange) onDateChange(newDate);
    if (onChange) onChange({ date: newDate, from: newDate, to: newDate });
  };

  const handleNextDay = () => {
    if (!date) return;
    const d = new Date(date);
    d.setDate(d.getDate() + 1);
    const newDate = toLocalYmd(d);
    if (onDateChange) onDateChange(newDate);
    if (onChange) onChange({ date: newDate, from: newDate, to: newDate });
  };

  return (
    <Wrap>
      {onModeChange && (
        <ModeToggle>
          <ModeBtn type="button" $active={mode === "day"} onClick={() => onModeChange("day")}>
            Day
          </ModeBtn>
          <ModeBtn type="button" $active={mode === "month"} onClick={() => onModeChange("month")}>
            Month
          </ModeBtn>
        </ModeToggle>
      )}

      <Presets aria-label="Report date presets">
        {presets.map((item) => (
          <PresetBtn
            type="button"
            key={item.id}
            $active={activePreset === item.id}
            onClick={() => handlePresetSelect(item)}
          >
            {item.label}
          </PresetBtn>
        ))}
      </Presets>

      {fromDate !== undefined && toDate !== undefined && (
        <CalendarCard>
          <FaCalendarAlt className="calendar-icon" />
          <LabelGroup>
            <span>From</span>
            <input
              aria-label="From date"
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => onChange && onChange({ from: e.target.value, to: toDate })}
            />
          </LabelGroup>
          <Divider>—</Divider>
          <LabelGroup>
            <span>To</span>
            <input
              aria-label="To date"
              type="date"
              value={toDate}
              min={fromDate}
              onChange={(e) => onChange && onChange({ from: fromDate, to: e.target.value })}
            />
          </LabelGroup>
        </CalendarCard>
      )}

      {date !== undefined && fromDate === undefined && (
        <CalendarCard>
          <NavBtn type="button" onClick={handlePrevDay} title="Previous Day">
            <FaChevronLeft size={10} />
          </NavBtn>
          <FaCalendarAlt className="calendar-icon" />
          <LabelGroup>
            <span>Date</span>
            <input
              aria-label="Select date"
              type="date"
              value={date}
              onChange={(e) => {
                const val = e.target.value;
                if (onDateChange) onDateChange(val);
                if (onChange) onChange({ date: val, from: val, to: val });
              }}
            />
          </LabelGroup>
          <NavBtn type="button" onClick={handleNextDay} title="Next Day">
            <FaChevronRight size={10} />
          </NavBtn>
        </CalendarCard>
      )}

      {month !== undefined && fromDate === undefined && date === undefined && (
        <CalendarCard>
          <FaCalendarAlt className="calendar-icon" />
          <LabelGroup>
            <span>Month</span>
            <input
              aria-label="Select month"
              type="month"
              value={month}
              onChange={(e) => {
                const val = e.target.value;
                if (onMonthChange) onMonthChange(val);
                if (onChange) onChange({ month: val });
              }}
            />
          </LabelGroup>
        </CalendarCard>
      )}
    </Wrap>
  );
}

const Wrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const ModeToggle = styled.div`
  display: flex;
  align-items: center;
  padding: 3px;
  border: 1px solid #c9dce8;
  border-radius: 12px;
  background: #f7fbfe;
`;

const ModeBtn = styled.button`
  border: 0;
  border-radius: 9px;
  padding: 7px 12px;
  background: ${(p) => (p.$active ? "#075f9e" : "transparent")};
  color: ${(p) => (p.$active ? "#ffffff" : "#526474")};
  font-size: 0.78rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: ${(p) => (p.$active ? "#ffffff" : "#075f9e")};
  }
`;

const Presets = styled.div`
  display: flex;
  align-items: center;
  padding: 3px;
  border: 1px solid #c9dce8;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(7, 95, 158, 0.06);
`;

const PresetBtn = styled.button`
  border: 0;
  border-radius: 9px;
  padding: 7px 12px;
  background: ${(p) => (p.$active ? "linear-gradient(135deg, #075f9e, #0786c5)" : "transparent")};
  color: ${(p) => (p.$active ? "#ffffff" : "#526474")};
  font-size: 0.78rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: ${(p) => (p.$active ? "#ffffff" : "#075f9e")};
    background: ${(p) => (p.$active ? "linear-gradient(135deg, #075f9e, #0786c5)" : "rgba(7, 95, 158, 0.08)")};
  }
`;

const CalendarCard = styled.div`
  min-height: 38px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border: 1px solid #c9dce8;
  border-radius: 12px;
  background: #ffffff;
  color: #5f7485;
  font-size: 0.78rem;
  box-shadow: 0 2px 6px rgba(7, 95, 158, 0.06);

  .calendar-icon {
    color: #075f9e;
    font-size: 0.9rem;
    flex-shrink: 0;
  }

  @media (max-width: 620px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const LabelGroup = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  color: #475569;

  span {
    font-size: 0.75rem;
    color: #64748b;
  }

  input {
    border: 0;
    background: transparent;
    color: #0f172a;
    font: inherit;
    font-weight: 700;
    outline: 0;
    min-width: 116px;
    cursor: pointer;
  }

  @media (max-width: 620px) {
    input {
      min-width: 0;
      width: 104px;
    }
  }
`;

const Divider = styled.span`
  color: #cbd5e1;
  font-weight: 700;
`;

const NavBtn = styled.button`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #075f9e;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #075f9e;
    color: #ffffff;
    border-color: #075f9e;
  }
`;
